import { useEffect, useState } from "react";
import "./bop020w020.css";
import { useLocation, useNavigate } from "react-router-dom";
import { BOP020W020Template } from "./bop020w020.template";
import {
  DEPRECATED_DINING_CATEGORY_LARGE,
  EXPENSE_TYPE,
  FOOD_CATEGORY_LARGE,
  INCOME_TYPE,
  type Category,
  normalizeCategoryLarge,
  normalizeType,
} from "../../common/commonConstant";
import useApiRequest from "../../common/useApiRequest";
import { useMaster } from "../../common/MasterContext";
import {
  type ExcludeCondition,
  type ReturnTo,
  type SearchCondition,
  RECENT_SEARCHES_STORAGE_KEY,
  createEmptyExcludeConditions,
  normalizeExcludeConditions,
} from "../navigationState";

export type { SearchCondition };

const MAX_RECENT_SEARCHES = 20;

/**
 * 機能ID: BOP020W020
 * 機能名: 収支一覧画面
 */
const BOP020W020 = () => {
  type RowId = string | number;
  type ResultRow = Record<string, any> & {
    __rowId: RowId;
    __deleteId: RowId | null;
  };

  const [type, setType] = useState<Category>(EXPENSE_TYPE);
  const [categoryLarge, setCategoryLarge] = useState("");
  const [categorySmall, setCategorySmall] = useState("");
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [memo, setMemo] = useState("");
  const [keihiFlg, setKeihiFlg] = useState<"" | "0" | "1">("");
  const [mikakuteiFlg, setMikakuteiFlg] = useState<"" | "0" | "1">("");
  const [excludeConditions, setExcludeConditions] = useState<
    ExcludeCondition[]
  >(createEmptyExcludeConditions);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState<RowId[]>([]);
  const [recentSearches, setRecentSearches] = useState<SearchCondition[]>(
    () => {
      try {
        const stored = localStorage.getItem(RECENT_SEARCHES_STORAGE_KEY);
        if (!stored) return [];
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed)
          ? parsed.slice(0, MAX_RECENT_SEARCHES).map((condition) => ({
              ...condition,
              type: normalizeType(condition.type),
              categoryLarge: normalizeCategoryLarge(
                condition.categoryLarge || "",
              ),
              keihiFlg:
                condition.keihiFlg === "0" || condition.keihiFlg === "1"
                  ? condition.keihiFlg
                  : "",
              mikakuteiFlg:
                condition.mikakuteiFlg === "0" || condition.mikakuteiFlg === "1"
                  ? condition.mikakuteiFlg
                  : "",
              // 除外条件追加前・1組だった頃に保存された履歴も3組の形式に揃える
              excludeConditions: normalizeExcludeConditions(condition),
            }))
          : [];
      } catch {
        return [];
      }
    },
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { execute } = useApiRequest();
  const { categorySmallList } = useMaster();
  // performSearchは食費/外食の互換検索で複数のexecute()を並行実行するため、
  // 1つのuseApiRequestインスタンス（最新呼び出し優先）が持つisLoadingではなく
  // 検索/削除処理全体を覆うローカルなisBusyで進行中かどうかを管理する。
  const [isBusy, setIsBusy] = useState(false);

  const handleBack = () => {
    const returnTo = (location.state as { returnTo?: ReturnTo } | null)
      ?.returnTo;
    if (returnTo?.pathname) {
      navigate(returnTo.pathname, {
        state: {
          month: returnTo.month,
          sortKey: returnTo.sortKey,
          sortDirection: returnTo.sortDirection,
          autoSearch: true,
        },
        replace: true,
      });
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const getMonthRange = (month: string) => {
    const [year, mon] = month.split("-");
    const monthNumber = Number(mon);
    const startDate = `${year}-${mon}-01`;
    const lastDay = new Date(Number(year), monthNumber, 0).getDate();
    const endDate = `${year}-${mon}-${String(lastDay).padStart(2, "0")}`;
    return { startDate, endDate };
  };

  const saveRecentSearch = (condition: SearchCondition) => {
    setRecentSearches((prev) => {
      const conditionKey = JSON.stringify(condition);
      const next = [
        condition,
        ...prev.filter((item) => JSON.stringify(item) !== conditionKey),
      ].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const trimRecentSearches = (count: number) => {
    setRecentSearches((prev) => {
      const next = prev.slice(0, count);
      if (next.length === prev.length) return prev;
      localStorage.setItem(RECENT_SEARCHES_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const applySearchCondition = (condition: SearchCondition) => {
    const normalizedType = normalizeType(condition.type);
    setType(normalizedType);
    setCategoryLarge(normalizeCategoryLarge(condition.categoryLarge));
    setCategorySmall(condition.categorySmall);
    setAmountFrom(condition.amountFrom);
    setAmountTo(condition.amountTo);
    setDateFrom(condition.dateFrom);
    setDateTo(condition.dateTo);
    setMemo(condition.memo);
    setKeihiFlg(
      normalizedType === INCOME_TYPE ? "" : (condition.keihiFlg ?? ""),
    );
    setMikakuteiFlg(condition.mikakuteiFlg ?? "");
    setExcludeConditions(normalizeExcludeConditions(condition));
  };

  // isBusyの管理は行わない検索本体。呼び出し元(performSearch/handleDelete)が
  // それぞれ自分の操作単位でisBusyを管理する。
  const performSearchCore = async ({
    type,
    categoryLarge,
    categorySmall,
    amountFrom,
    amountTo,
    dateFrom,
    dateTo,
    memo,
    keihiFlg,
    mikakuteiFlg,
    excludeConditions,
  }: SearchCondition, skipRecentSave = false) => {
    const normalizedCategoryLarge = normalizeCategoryLarge(categoryLarge);
    const categoryLargeSearchValues =
      type === EXPENSE_TYPE && normalizedCategoryLarge === FOOD_CATEGORY_LARGE
        ? [FOOD_CATEGORY_LARGE, DEPRECATED_DINING_CATEGORY_LARGE]
        : [normalizedCategoryLarge];

    const results = await Promise.all(
      categoryLargeSearchValues.map((searchCategoryLarge) =>
        execute(`${process.env.REACT_APP_API_BASE_URL}/api/bop020rs02001`, {
          method: "POST",
          body: JSON.stringify({
            type,
            categoryLarge: searchCategoryLarge,
            categorySmall,
            amountFrom: amountFrom ? Number(amountFrom) : null,
            amountTo: amountTo ? Number(amountTo) : null,
            dateFrom: dateFrom || null,
            dateTo: dateTo || null,
            memo,
            keihiFlg: keihiFlg !== "" ? Number(keihiFlg) : null,
            mikakuteiFlg: mikakuteiFlg !== "" ? Number(mikakuteiFlg) : null,
          }),
        }),
      ),
    );

    const failedResult = results.find((result) => !result.ok);
    if (failedResult && !failedResult.ok) {
      alert(failedResult.error.message);
      return undefined;
    }

    const data = results.flatMap((result) =>
      result.ok && Array.isArray(result.data) ? result.data : [],
    );

    const uniqueData = Array.from(
      new Map(
        data.map((row, index) => [
          row.shushiNo ?? row.id ?? `fallback-${index}`,
          row,
        ]),
      ).values(),
    );

    // 除外条件はAPIの検索結果に対して画面側で適用し、いずれかの組に該当する行を除く（OR条件）。
    // 項目（大）は正規化後の値で比較し、「食費」を除外した場合は旧項目名「外食費」で
    // 登録された行も除外されるようにする
    const normalizedExcludeConditions = normalizeExcludeConditions({
      excludeConditions,
    });
    const activeExcludeConditions = normalizedExcludeConditions.filter(
      (condition) => condition.categoryLarge !== "",
    );
    const filteredData = uniqueData.filter(
      (row) =>
        !activeExcludeConditions.some(
          (condition) =>
            normalizeCategoryLarge(row.categoryLarge || "") ===
              condition.categoryLarge &&
            (condition.categorySmall === "" ||
              (row.categorySmall || "") === condition.categorySmall),
        ),
    );

    setResults(
      filteredData.map((row, index) => {
        const deleteId = row.shushiNo ?? row.id ?? null;
        const rowId =
          deleteId ??
          [
            "row",
            index,
            row.type,
            row.categoryLarge,
            row.categorySmall,
            row.amount,
            row.date,
            row.updated,
          ].join("-");

        return {
          ...row,
          type: normalizeType(row.type),
          categoryLarge: normalizeCategoryLarge(row.categoryLarge || ""),
          __rowId: rowId,
          __deleteId: deleteId,
        };
      }),
    );
    setSelectedRowIds([]);
    setCurrentPage(1);
    setHasSearched(true);
    if (!skipRecentSave) {
      saveRecentSearch({
        type,
        categoryLarge: normalizedCategoryLarge,
        categorySmall,
        amountFrom,
        amountTo,
        dateFrom,
        dateTo,
        memo,
        keihiFlg,
        mikakuteiFlg,
        excludeConditions: normalizedExcludeConditions,
      });
    }
    return filteredData.length;
  };

  const performSearch = async (
    condition: SearchCondition,
    skipRecentSave = false,
  ): Promise<number | undefined> => {
    setIsBusy(true);
    try {
      return await performSearchCore(condition, skipRecentSave);
    } finally {
      setIsBusy(false);
    }
  };

  const handleTypeChange = (newType: Category) => {
    setType(newType);
    setCategoryLarge("");
    setCategorySmall("");
    if (newType === INCOME_TYPE) {
      setKeihiFlg("");
    }
    setMikakuteiFlg("");
    setExcludeConditions(createEmptyExcludeConditions());
  };

  const handleCategoryLargeChange = (value: string) => {
    setCategoryLarge(value);
    setCategorySmall("");
  };

  // 項目（大）を変更した組は、項目（小）の選択を解除する
  const handleExcludeConditionChange = (
    index: number,
    field: keyof ExcludeCondition,
    value: string,
  ) => {
    setExcludeConditions((prev) =>
      prev.map((condition, i) => {
        if (i !== index) return condition;
        return field === "categoryLarge"
          ? { categoryLarge: value, categorySmall: "" }
          : { ...condition, categorySmall: value };
      }),
    );
  };

  const handleClear = () => {
    setType(EXPENSE_TYPE);
    setCategoryLarge("");
    setCategorySmall("");
    setAmountFrom("");
    setAmountTo("");
    setDateFrom("");
    setDateTo("");
    setMemo("");
    setKeihiFlg("");
    setMikakuteiFlg("");
    setExcludeConditions(createEmptyExcludeConditions());
    setResults([]); // 検索結果もクリア
    setSelectedRowIds([]);
    setCurrentPage(1);
    setHasSearched(false);
  };

  // 除外条件の各組を検索条件と突き合わせ、結果が常に0件になる指定であればエラーメッセージを返す
  const validateExcludeConditions = () => {
    const normalizedSearchLarge = normalizeCategoryLarge(categoryLarge);
    for (let index = 0; index < excludeConditions.length; index++) {
      const condition = excludeConditions[index];
      const excludeLarge = normalizeCategoryLarge(condition.categoryLarge);
      if (excludeLarge === "" || excludeLarge !== normalizedSearchLarge) {
        continue;
      }
      const label = `除外条件${index + 1}`;
      // 検索条件と同じ項目（大）・項目（小）の組み合わせを除外すると結果が常に0件になる
      if (condition.categorySmall === categorySmall) {
        return `${label}が検索条件と重複しています。項目（大）・項目（小）の組み合わせを見直してください`;
      }
      // 検索条件で項目（小）まで指定し、除外条件で同じ項目（大）全体を除外すると結果が常に0件になる。
      // 項目（大）配下の項目（小）が1つだけなら両者は同じ指定とみなせるため許容し、
      // 複数ある場合は除外条件の項目（小）の指定を求める
      if (condition.categorySmall === "" && categorySmall !== "") {
        const smallCount = categorySmallList.filter(
          (item) =>
            normalizeType(item.type) === type &&
            normalizeCategoryLarge(item.category) === excludeLarge,
        ).length;
        if (smallCount > 1) {
          return `${label}が検索条件と重複しています。${label}の項目（小）を指定してください`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const excludeError = validateExcludeConditions();
    if (excludeError) {
      alert(excludeError);
      return;
    }
    const effectiveKeihiFlg = type === INCOME_TYPE ? "" : keihiFlg;
    await performSearch({
      type,
      categoryLarge,
      categorySmall,
      amountFrom,
      amountTo,
      dateFrom,
      dateTo,
      memo,
      keihiFlg: effectiveKeihiFlg,
      mikakuteiFlg,
      excludeConditions,
    });
  };

  const handleRecentSearchClick = (condition: SearchCondition) => {
    applySearchCondition(condition);
  };

  const handleItemsPerPageChange = (value: number) => {
    setItemsPerPage(value);
    setCurrentPage(1); // 行数変更時は1ページ目に戻る
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      // 同じキーをクリックした場合は方向を反転
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // 異なるキーをクリックした場合は昇順でソート
      setSortKey(key);
      setSortDirection("asc");
    }
    setCurrentPage(1); // ソート時は1ページ目に戻る
  };

  // ソートされたデータを取得
  const getSortedResults = () => {
    if (!sortKey) return results;

    return [...results].sort((a, b) => {
      let aValue = a[sortKey];
      let bValue = b[sortKey];

      // 金額の場合は数値として比較
      if (sortKey === "amount") {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      // 日付の場合は日付として比較
      else if (sortKey === "date") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
      }
      // その他の場合は文字列として比較
      else {
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  };

  // ページ分割されたデータを計算
  const sortedResults = getSortedResults();
  const totalPages = Math.ceil(sortedResults.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedResults = sortedResults.slice(startIndex, endIndex);

  const getRowId = (row: ResultRow): RowId => row.__rowId;

  const allChecked =
    paginatedResults.length > 0 &&
    paginatedResults.every((row) => selectedRowIds.includes(getRowId(row)));

  const isRowChecked = (row: ResultRow) =>
    selectedRowIds.includes(getRowId(row));

  const handleToggleAll = (checked: boolean) => {
    const pageIds = paginatedResults.map((row) => getRowId(row));

    setSelectedRowIds((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...pageIds]));
      }
      return prev.filter((id) => !pageIds.includes(id));
    });
  };

  const handleToggleRow = (id: RowId, checked: boolean) => {
    setSelectedRowIds((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }
      return prev.filter((item) => item !== id);
    });
  };

  const handleDetailClick = (row: any) => {
    const existingReturnTo = (location.state as { returnTo?: ReturnTo } | null)
      ?.returnTo;
    const returnTo: ReturnTo = {
      pathname: "/bop020w020",
      searchSnapshot: {
        type,
        categoryLarge,
        categorySmall,
        amountFrom,
        amountTo,
        dateFrom,
        dateTo,
        memo,
        keihiFlg,
        mikakuteiFlg,
        excludeConditions,
      },
      page: currentPage,
      sortKey,
      sortDirection,
      returnTo: existingReturnTo,
    };
    navigate("/bop020w010", { state: { record: row, returnTo } });
  };

  const handleDelete = async () => {
    const selectedCount = selectedRowIds.length;

    if (selectedCount === 0) {
      alert("削除するレコードを選択してください");
      return;
    }

    const confirmed = window.confirm(
      `${selectedCount}件のレコードを削除してもよろしいですか？`,
    );
    if (!confirmed) {
      return;
    }

    const selectedDeleteIds = results
      .filter((row) => selectedRowIds.includes(getRowId(row)))
      .map((row) => row.__deleteId)
      .filter((id): id is RowId => id !== null);

    if (selectedDeleteIds.length !== selectedCount) {
      alert("削除対象のレコードIDを取得できませんでした");
      return;
    }

    setIsBusy(true);
    try {
      const result = await execute(
        `${process.env.REACT_APP_API_BASE_URL}/api/bop020rs02002`,
        {
          method: "POST",
          body: JSON.stringify({ shushiNoList: selectedDeleteIds }),
        },
      );

      if (!result.ok) {
        alert(result.error.message);
        return;
      }

      alert(`${selectedCount}件削除しました`);
      setSelectedRowIds([]);
      // 検索結果を再取得（isBusyは既にこの関数が管理しているのでperformSearchCoreを直接呼ぶ）
      await performSearchCore({
        type,
        categoryLarge,
        categorySmall,
        amountFrom,
        amountTo,
        dateFrom,
        dateTo,
        memo,
        keihiFlg,
        mikakuteiFlg,
        excludeConditions,
      });
    } finally {
      setIsBusy(false);
    }
  };

  useEffect(() => {
    let ignore = false;

    const state = location.state as
      | {
          record?: any;
          month?: string;
          searchSnapshot?: SearchCondition;
          page?: number;
          sortKey?: string;
          sortDirection?: "asc" | "desc";
          autoSearch?: boolean;
        }
      | null;
    if (!state) {
      return;
    }

    // 収支登録画面での更新完了・戻るボタンから復帰した場合は、遷移前の検索条件で再検索する
    if (state.searchSnapshot && state.autoSearch) {
      const rawCondition = state.searchSnapshot;
      const normalizedType = normalizeType(rawCondition.type);
      const condition: SearchCondition = {
        ...rawCondition,
        type: normalizedType,
        categoryLarge: normalizeCategoryLarge(rawCondition.categoryLarge),
        keihiFlg:
          normalizedType === INCOME_TYPE ? "" : (rawCondition.keihiFlg ?? ""),
        mikakuteiFlg: rawCondition.mikakuteiFlg ?? "",
        excludeConditions: normalizeExcludeConditions(rawCondition),
      };
      applySearchCondition(condition);

      const restorePageAndSort = (resultCount?: number) => {
        if (ignore) {
          return;
        }
        const requestedPage = typeof state.page === "number" ? state.page : 1;
        // 編集によって検索結果件数が変わっている場合に備え、新しい件数に収まる範囲へ丸める
        const newTotalPages =
          typeof resultCount === "number"
            ? Math.max(1, Math.ceil(resultCount / itemsPerPage))
            : requestedPage;
        setCurrentPage(Math.min(requestedPage, newTotalPages));
        if (state.sortKey) {
          setSortKey(state.sortKey);
          setSortDirection(state.sortDirection || "asc");
        }
      };

      performSearch(condition, true)
        .then(restorePageAndSort)
        .catch(() => restorePageAndSort());
      return () => {
        ignore = true;
      };
    }

    const record = state.record;
    const month = state.month;
    if (!record || !month) {
      return;
    }

    const recordType = normalizeType(record.type);
    const recordCategoryLarge = normalizeCategoryLarge(
      record.categoryLarge || "",
    );
    const recordCategorySmall = record.categorySmall || "";
    const { startDate, endDate } = getMonthRange(month);

    setType(recordType);
    setCategoryLarge(recordCategoryLarge);
    setCategorySmall(recordCategorySmall);
    setAmountFrom("");
    setAmountTo("");
    setDateFrom(startDate);
    setDateTo(endDate);
    setMemo("");
    setExcludeConditions(createEmptyExcludeConditions());

    performSearch({
      type: recordType,
      categoryLarge: recordCategoryLarge,
      categorySmall: recordCategorySmall,
      amountFrom: "",
      amountTo: "",
      dateFrom: startDate,
      dateTo: endDate,
      memo: "",
      keihiFlg: "",
      mikakuteiFlg: "",
      excludeConditions: createEmptyExcludeConditions(),
    });

    return () => {
      ignore = true;
    };
    // performSearch/applySearchConditionは毎レンダー再生成されるが、
    // 参照するのはexecute（安定）と正規化関数（純粋関数）のみのため安全
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <BOP020W020Template
      type={type}
      isLoading={isBusy}
      categoryLarge={categoryLarge}
      categorySmall={categorySmall}
      amountFrom={amountFrom}
      amountTo={amountTo}
      dateFrom={dateFrom}
      dateTo={dateTo}
      memo={memo}
      recentSearches={recentSearches}
      results={paginatedResults}
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      totalItems={results.length}
      sortKey={sortKey}
      sortDirection={sortDirection}
      allChecked={allChecked}
      isRowChecked={isRowChecked}
      onTypeChange={handleTypeChange}
      onCategoryLargeChange={handleCategoryLargeChange}
      onCategorySmallChange={setCategorySmall}
      onAmountFromChange={setAmountFrom}
      onAmountToChange={setAmountTo}
      onDateFromChange={setDateFrom}
      onDateToChange={setDateTo}
      onMemoChange={setMemo}
      keihiFlg={keihiFlg}
      onKeihiFlgChange={setKeihiFlg}
      mikakuteiFlg={mikakuteiFlg}
      onMikakuteiFlgChange={setMikakuteiFlg}
      excludeConditions={excludeConditions}
      onExcludeConditionChange={handleExcludeConditionChange}
      onClear={handleClear}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onRecentSearchClick={handleRecentSearchClick}
      onRecentSearchesTrim={trimRecentSearches}
      onDetailClick={handleDetailClick}
      onItemsPerPageChange={handleItemsPerPageChange}
      onPageChange={handlePageChange}
      onSort={handleSort}
      onToggleAll={handleToggleAll}
      onToggleRow={handleToggleRow}
      onDelete={handleDelete}
      showResults={hasSearched}
    />
  );
};

export default BOP020W020;
